import {
  MD3DarkTheme,
  MD3LightTheme as DefaultTheme,
  MD3Theme,
} from 'react-native-paper';
import { Colors } from './colors';
import { colors as CropCircleColors } from '../components/crop-circle/tokens/colors';
import { roleColorValues, type ThemeMode } from './roleTokenValues';

/**
 * Paper's entire MD3 colour contract, resolved from Crop Circle role tokens.
 *
 * Every key Paper can paint with is named here, for both modes, so the role
 * tokens are the single source of truth for the ~245 files that render
 * `react-native-paper` components. Before this, only dark was mapped (11 of
 * 33 keys) and light fell through to `MD3LightTheme`, which meant a role-token
 * edit moved Crop Circle components but silently left every Paper component
 * behind. Light matched only because its role values were seeded from what
 * production already rendered: a coincidence that had to be hand-maintained,
 * with nothing to catch it drifting.
 *
 * Values are static hex from `roleColorValues`, NEVER `Colors.role`: on web
 * the latter carries `var()` strings, and Paper runs theme colours through the
 * `color` library for ripple/alpha maths, which throws `Unable to parse color
 * from string: var(--x)` and error-boundaries the whole app. Callers pass the
 * resolved mode in, so the correct static map is always chosen; the theme
 * re-renders on a mode flip because `resolvedTheme` changes identity.
 *
 * One key still reads a Crop Circle primitive rather than a role token:
 * `shadow`, which is a cast shadow rather than a surface and has no role
 * naming it. Everything else resolves through `roleColorValues`.
 *
 * Mode-constant keys, deliberately: `primary`, `onPrimary` and `inversePrimary`
 * (brand orange and white-on-brand read on either ground), `shadow`, and
 * `secondaryContainer`. `onPrimaryContainer` is not one of them: it inverts,
 * because the 10% wash it sits on does.
 */
const paperColorsFromRoles = (mode: ThemeMode): MD3Theme['colors'] => {
  const r = roleColorValues[mode];

  return {
    // brand
    primary: r.brandOnSurface,
    onPrimary: r.textOnBrand,
    primaryContainer: r.brandTint,
    onPrimaryContainer: r.textOnBrandTint,
    inversePrimary: r.brandOnSurface,

    // secondary: Paper's tonal buttons, selected chips and icon buttons.
    //
    // `secondaryContainer` stays `transparent` as a checked decision, not an
    // inheritance. Nothing renders it today: `contained-tonal` appears only as
    // a type-union member in `modalButton.tsx`, and there are no
    // `SegmentedButtons`, no `selected` Chips and no `selected` IconButtons in
    // the tree. Keeping it preserves current output exactly. The token-correct
    // value the day one of those is adopted is `surfaceSelected`; changing it
    // now would only add an unreviewed fill to components nobody renders.
    secondary: r.textSecondary,
    onSecondary: r.textOnInverse,
    secondaryContainer: 'transparent',
    onSecondaryContainer: r.textPrimary,

    // tertiary: unused by Gripp today, mapped so it cannot fall back to
    // Material's pink if a component starts reading it.
    tertiary: r.textSecondary,
    onTertiary: r.textOnInverse,
    tertiaryContainer: r.surfaceRaised,
    onTertiaryContainer: r.textPrimary,

    // surfaces
    background: r.canvas,
    onBackground: r.textPrimary,
    surface: r.surface,
    onSurface: r.textPrimary,
    surfaceVariant: r.surfaceRaised,
    onSurfaceVariant: r.textSecondary,
    // Opaque, unlike Material's translucent `onSurface` at 38%/12%. A
    // translucent disabled colour picks up whatever sits behind it, which is
    // exactly the mode-dependence role tokens exist to remove.
    //
    // `surfaceRaised`, NOT `fillDisabled`: this is the same pairing Crop
    // Circle's own Button already ships for its disabled state, so a Paper
    // button and a Crop Circle button finally render alike. `fillDisabled` is
    // tuned as a fill against #262626 inputs and drops to ~1.3:1 against
    // `textDisabled` here, which is why Button rejected it (see
    // `Button.tsx`'s `getTextColor`). Crop Circle's TextInput instead keeps
    // `inputBackground` when disabled, but the two sit a hair apart in both
    // modes (#ECEEF1 vs #F3F3F3, #232323 vs #262626), so Button's documented
    // requirement decides the shared key.
    surfaceDisabled: r.surfaceRaised,
    onSurfaceDisabled: r.textDisabled,

    // error: `textDestructive`, not `semantic.error`. Paper reads this key as a
    // foreground (HelperText, TextInput error labels) far more than as a fill,
    // and the raw danger red is 3.94:1 on white, which put every error label
    // below AA. The role token is AA in both modes, and Badge (the one fill
    // consumer) gets more contrast against `onError`, not less.
    error: r.textDestructive,
    // `textOnInverse`, not `textOnBrand`. `textDestructive` inverts across
    // modes (deep red on light, coral on dark) exactly like `surfaceInverse`
    // does, so a constant white foreground fails on the dark fill: Paper's
    // Badge paints `onError` on an `error` ground, and #FFFFFF on #EB836B is
    // 2.63:1. `textOnInverse` follows the flip and is AA on both (6.29:1 and
    // 6.62:1).
    onError: r.textOnInverse,
    errorContainer: r.surfaceDestructiveSoft,
    onErrorContainer: r.textDestructive,

    // borders
    outline: r.borderControl,
    // `borderSection`, not `borderSubtle`: Paper's Divider is effectively this
    // key's only consumer, and Crop Circle's own Divider paints
    // `borderSection`. Same "render alike" reasoning as `surfaceDisabled`
    // below. `borderSubtle` is the row-divider token and is a step fainter
    // (1.11:1 vs 1.16:1 on white), which left a Paper Divider visibly weaker
    // than a Crop Circle one on the same screen.
    outlineVariant: r.borderSection,

    // inverse fills
    inverseSurface: r.surfaceInverse,
    inverseOnSurface: r.textOnInverse,

    // overlays
    shadow: CropCircleColors.neutral[800],
    scrim: r.scrim,
    backdrop: r.backdrop,

    elevation: {
      level0: 'transparent',
      level1: r.surface,
      level2: r.surfaceRaised,
      level3: r.surfaceRaised,
      level4: r.surfaceSelected,
      level5: r.surfaceActive,
    },
  };
};

/**
 * Resolved once per mode rather than per `createBaseTheme` call. The map is a
 * pure function of a two-value enum, and `createBaseTheme` is called on every
 * render at `apps/gripp-web/src/dashboard/components/layout.tsx` (gripp-app
 * memoises; web does not), so rebuilding 33 keys there was pure waste. Shape
 * mirrors `roleColorValues`, which this reads from.
 */
const paperColors: Record<ThemeMode, MD3Theme['colors']> = {
  light: paperColorsFromRoles('light'),
  dark: paperColorsFromRoles('dark'),
};

/**
 * Generate theme with optional Inter fonts
 * @param useInterFonts - If true, uses Inter font family. If false, uses system default.
 * @param mode - selects which side of the role-token maps Paper is painted
 * from. Both modes take the same path; see `paperColorsFromRoles`.
 */
export const createBaseTheme = (
  useInterFonts = false,
  mode: ThemeMode = 'light'
): MD3Theme => {
  const fontFamily = useInterFonts ? 'Inter' : undefined;
  const base = mode === 'dark' ? MD3DarkTheme : DefaultTheme;

  // `Colors.role` is a nested object, and on web its values are `var()`
  // strings. Nothing in Paper reads `colors.role`, so spreading it in was
  // inert - but it seeded `MD3Theme['colors']` with the one value shape
  // Paper's colour maths cannot parse, one internal iteration away from the
  // crash `useRoleHex` exists to prevent. Keep the theme flat and hex-only.
  const { role: _role, ...flatColors } = Colors;

  return {
    ...base,
    colors: {
      ...base.colors,
      // Gripp's hue-named legacy keys. None of them collide with the MD3
      // contract except `primary` and `secondaryContainer`, and the role map
      // below restates both, so this spread can only add non-MD3 keys for the
      // handful of call sites that read them off the theme. It shrinks to
      // nothing as those migrate.
      ...flatColors,
      ...paperColors[mode],
    },
    roundness: 8,
    fonts: {
      ...DefaultTheme.fonts,
      default: {
        ...DefaultTheme.fonts.default,
        ...(fontFamily && { fontFamily }),
      },
      displayLarge: {
        ...DefaultTheme.fonts.displayLarge,
        ...(fontFamily && { fontFamily }),
      },
      displayMedium: {
        ...DefaultTheme.fonts.displayMedium,
        ...(fontFamily && { fontFamily }),
      },
      displaySmall: {
        ...DefaultTheme.fonts.displaySmall,
        ...(fontFamily && { fontFamily }),
      },
      headlineLarge: {
        ...DefaultTheme.fonts.headlineLarge,
        ...(fontFamily && { fontFamily }),
      },
      headlineMedium: {
        ...DefaultTheme.fonts.headlineMedium,
        ...(fontFamily && { fontFamily }),
      },
      headlineSmall: {
        ...DefaultTheme.fonts.headlineSmall,
        ...(fontFamily && { fontFamily }),
      },
      titleLarge: {
        ...DefaultTheme.fonts.titleLarge,
        ...(fontFamily && { fontFamily }),
      },
      titleMedium: {
        ...DefaultTheme.fonts.titleMedium,
        ...(fontFamily && { fontFamily }),
      },
      titleSmall: {
        ...DefaultTheme.fonts.titleSmall,
        ...(fontFamily && { fontFamily }),
      },
      bodyLarge: {
        ...DefaultTheme.fonts.bodyLarge,
        ...(fontFamily && { fontFamily }),
      },
      bodyMedium: {
        ...DefaultTheme.fonts.bodyMedium,
        ...(fontFamily && { fontFamily }),
      },
      bodySmall: {
        ...DefaultTheme.fonts.bodySmall,
        ...(fontFamily && { fontFamily }),
      },
      labelLarge: {
        ...DefaultTheme.fonts.labelLarge,
        ...(fontFamily && { fontFamily }),
      },
      labelMedium: {
        ...DefaultTheme.fonts.labelMedium,
        ...(fontFamily && { fontFamily }),
      },
      labelSmall: {
        ...DefaultTheme.fonts.labelSmall,
        ...(fontFamily && { fontFamily }),
      },
    },
  };
};

// Default export with Inter fonts enabled
export const BaseTheme: MD3Theme = createBaseTheme(false);
